// Component for SideBar History
import * as React from 'react';

import { Box, Grid, IconButton, Paper, TextField, Typography, Switch, FormControl, FormControlLabel, FormGroup } from '@mui/material';
import { styled } from '@mui/material/styles';
import { theme } from '../../theme/theme';
import moment from 'moment';
import { VList } from "virtua";

import { ReactComponent as OverallOnlineHealthyIcon } from '../../assets/OverallHealthyIcon_Online.svg';
import { ReactComponent as OverallOnlineWarningIcon } from '../../assets/OverallWarningIcon_Online.svg';
import { ReactComponent as OverallOnlineErrorIcon } from '../../assets/OverallErrorIcon_Online.svg';
import { ReactComponent as AirplaneIcon } from '../../assets/AirplaneOfflineIcon.svg';
import { ReactComponent as ACIDTransferSessionIcon } from '../../assets/ACIDTransferSessionIcon.svg';
import { ReactComponent as ActionAcknowledgedIcon } from '../../assets/ActionAcknowledgedIcon.svg';
import { ReactComponent as ActionFailedIcon } from '../../assets/ActionFailedIcon.svg';
import { ReactComponent as ActionFinishedIcon } from '../../assets/ActionFinishedIcon.svg';
import { ReactComponent as ActionInitiatedIcon } from '../../assets/ActionInitiatedIcon.svg';
import { ReactComponent as CommissionedIcon } from '../../assets/CommissionedIcon.svg';
import { ReactComponent as DecommissionedIcon } from '../../assets/DecommissionedIcon.svg';
import { ReactComponent as NetworkIcon } from '../../assets/NetworkConnectivityIcon.svg';
import { ReactComponent as SettingsChangedIcon } from '../../assets/SettingsChangedIcon.svg';
import { ReactComponent as SoftwareVersionChangedIcon } from '../../assets/SoftwareVersionChangedIcon.svg';
import { ReactComponent as VisionCatalogChangedIcon } from '../../assets/VisionCatalogChangedIcon.svg';
import { ReactComponent as EdgeContentVersionChangedIcon } from '../../assets/EdgeContentChangedIcon.svg';
import { ReactComponent as VPNStatusIcon } from '../../assets/VPNStatusIcon.svg';
import { ReactComponent as ArchivedIcon } from '../../assets/ArchivedIcon.svg';
import { ReactComponent as SoftwareUpgradeFailedIcon } from '../../assets/SWUpgradeFailedIcon.svg';

import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import InfoIcon from '@mui/icons-material/Info';

const DarkSwitch = styled(Switch)(({ theme }) => ({
  '& .MuiSwitch-switchBase': {
    color: theme.palette.sidebarContent.labelText,
  },
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: theme.palette.sidebarContent.valueText,
  },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: theme.palette.sidebarContent.valueText,
  },
}));

// JS doesn't have interfaces
//  use this commented out definition as a reference for the props that should be accepted for this component 
// export interface SideBarHistoryProps {
//   cloudSystemDetails : CloudSystem
// }

const SideBarHistory = (props) => {

  const [sortOrder, setSortOrder] = React.useState('desc'); // 'asc' or 'desc'
  const [searchText, setSearchText] = React.useState('');
  const [showAircraftEntries, setShowAircraftEntries] = React.useState(false);
  
  const getCardStyling = (historyEntry) => {
    var styles = {
      title: '',
      textColor: '#FFFFFF',
      secondaryTextColor: '#AEB1B4',
      backgroundColor: 'rgba(174, 177, 180, .15)', // use rgba to apply opacity
      borderLeftColor: '#AEB1B4',
      icon: <InfoIcon sx={{ color: '#AEB1B4' }} preserveAspectRatio={'xMidYMid meet'}  height='20px' width='20px'/>,
    };

    switch (historyEntry.type) {
      case "VPNStatusChanged":
        // Green or grey
        styles.title = 'VPN Status Changed';
        // Check if Up or Down
        const isUp = historyEntry?.message?.includes('to "up"') || historyEntry?.message?.includes("to 'up'");
        styles.backgroundColor = isUp ? 'rgba(82, 179, 91, .15)' : 'rgba(174, 177, 180, .15)';
        styles.borderLeftColor = isUp ? '#52B35B' : '#AEB1B4';
        styles.icon = isUp ? <VPNStatusIcon preserveAspectRatio={'xMidYMid meet'} height='20px' width='20px' color='#52B35B' /> : <VPNStatusIcon preserveAspectRatio={'xMidYMid meet'} height='20px' width='20px' color='#AEB1B4' />;
        break;
      case "VisionCatalogChanged":
        // Blue
        styles.title = 'Vision Catalog Changed';
        styles.backgroundColor = 'rgba(85, 167, 223, .15)';
        styles.borderLeftColor = '#55A7DF';
        styles.icon = <VisionCatalogChangedIcon preserveAspectRatio={'xMidYMid meet'} height='20px' width='20px' color='#55A7DF' />;
        break;
      case "EdgeContentVersionChanged":
        // Blue
        styles.title = 'Edge Content Changed';
        styles.backgroundColor = 'rgba(85, 167, 223, .15)';
        styles.borderLeftColor = '#55A7DF';
        styles.icon = <EdgeContentVersionChangedIcon preserveAspectRatio={'xMidYMid meet'} height='20px' width='20px' color='#55A7DF' />;
        break;
      case "SoftwareVersionChanged":
        // Blue
        styles.title = 'Software Version Changed';
        styles.backgroundColor = 'rgba(85, 167, 223, .15)';
        styles.borderLeftColor = '#55A7DF';
        styles.icon = <SoftwareVersionChangedIcon preserveAspectRatio={'xMidYMid meet'} height='20px' width='20px' color='#55A7DF' />;
        break;
      case "Online":
        // Green
        styles.title = 'Online';
        styles.backgroundColor = 'rgba(82, 179, 91, .15)';
        styles.borderLeftColor = '#52B35B';
        styles.icon = <NetworkIcon preserveAspectRatio={'xMidYMid meet'} height='20px' width='20px' color='#52B35B' />;
        break;
      case "Offline":
        // Grey -- default
        styles.title = 'Offline';
        styles.icon = <NetworkIcon preserveAspectRatio={'xMidYMid meet'} height='20px' width='20px' color='#AEB1B4' />;
        break;
      case "HealthMetricHealthy":
        // Green
        styles.title = 'Health Metric Healthy';
        styles.backgroundColor = 'rgba(82, 179, 91, .15)';
        styles.borderLeftColor = '#52B35B';
        styles.icon = <OverallOnlineHealthyIcon preserveAspectRatio={'xMidYMid meet'} height='20px' width='20px' color='#52B35B' />;
        break;
      case "HealthMetricWarning":
        // Yellow
        styles.title = 'Health Metric Warning';
        styles.backgroundColor = 'rgba(234, 171, 0, .15)';
        styles.borderLeftColor = '#EAAB00';
        styles.icon = <OverallOnlineWarningIcon preserveAspectRatio={'xMidYMid meet'} height='20px' width='20px' color='#EAAB00' />;
        break;
      case "HealthMetricError":
        // Red
        styles.title = 'Health Metric Error';
        styles.backgroundColor = 'rgba(213, 45, 52, .15)';
        styles.borderLeftColor = '#D52D34';
        styles.icon = <OverallOnlineErrorIcon preserveAspectRatio={'xMidYMid meet'} height='20px' width='20px' color='#D52D34' />;
        break;
      case "ACIDConnected":
        // Grey -- default
        styles.title = 'Aircraft Connected';
        styles.icon = <AirplaneIcon preserveAspectRatio={'xMidYMid meet'} height='20px' width='20px' color='#AEB1B4' />;
        break;
      case "ACIDTransferSession":
        // Grey -- default
        styles.title = 'Content Transfer Session';
        styles.icon = <ACIDTransferSessionIcon preserveAspectRatio={'xMidYMid meet'} height='20px' width='20px' color='#AEB1B4' />;
        break;
      case "Commissioned":
        // Blue
        styles.title = 'Commissioned';
        styles.backgroundColor = 'rgba(85, 167, 223, .15)';
        styles.borderLeftColor = '#55A7DF';
        styles.icon = <CommissionedIcon preserveAspectRatio={'xMidYMid meet'} height='20px' width='20px' color='#55A7DF' />;
        break;
      case "BoxSwap":
        // Blue
        styles.title = 'Box Swap';
        styles.backgroundColor = 'rgba(85, 167, 223, .15)';
        styles.borderLeftColor = '#55A7DF';
        styles.icon = <CommissionedIcon preserveAspectRatio={'xMidYMid meet'} height='20px' width='20px' color='#55A7DF' />;
        break;
      case "Decommissioned":
        // Red
        styles.title = 'Decommissioned';
        styles.backgroundColor = 'rgba(213, 45, 52, .15)';
        styles.borderLeftColor = '#D52D34';
        styles.icon = <DecommissionedIcon preserveAspectRatio={'xMidYMid meet'} height='20px' width='20px' color='#D52D34' />;
        break;
      case "ActionInitiated":
        // Blue
        styles.title = 'Action Initiated';
        styles.backgroundColor = 'rgba(85, 167, 223, .15)';
        styles.borderLeftColor = '#55A7DF';
        styles.icon = <ActionInitiatedIcon preserveAspectRatio={'xMidYMid meet'} height='20px' width='20px' color='#55A7DF' />;
        break;
      case "ActionAcknowledged":
        // Blue
        styles.title = 'Action Acknowledged';
        styles.backgroundColor = 'rgba(85, 167, 223, .15)';
        styles.borderLeftColor = '#55A7DF';
        styles.icon = <ActionAcknowledgedIcon preserveAspectRatio={'xMidYMid meet'} height='20px' width='20px' color='#55A7DF' />;
        break;
      case "ActionFinished":
        // Green
        styles.title = 'Action Finished';
        styles.backgroundColor = 'rgba(82, 179, 91, .15)';
        styles.borderLeftColor = '#52B35B';
        styles.icon = <ActionFinishedIcon preserveAspectRatio={'xMidYMid meet'} height='20px' width='20px' color='#52B35B' />;
        break;
      case "ActionFailed":
        // Red
        styles.title = 'Action Failed';
        styles.backgroundColor = 'rgba(213, 45, 52, .15)';
        styles.borderLeftColor = '#D52D34';
        styles.icon = <ActionFailedIcon preserveAspectRatio={'xMidYMid meet'} height='20px' width='20px' color='#D52D34' />;
        break;
      case "ActionCancelled":
        // Red
        styles.title = 'Action Cancelled';
        styles.backgroundColor = 'rgba(213, 45, 52, .15)';
        styles.borderLeftColor = '#D52D34';
        styles.icon = <ActionFailedIcon preserveAspectRatio={'xMidYMid meet'} height='20px' width='20px' color='#D52D34' />;
        break;
      case "SettingsChanged":
        // Blue
        styles.title = 'Settings Changed';
        styles.backgroundColor = 'rgba(85, 167, 223, .15)';
        styles.borderLeftColor = '#55A7DF';
        styles.icon = <SettingsChangedIcon preserveAspectRatio={'xMidYMid meet'} height='20px' width='20px' color='#55A7DF' />;
        break;
      case "Archived":
        // Grey
        styles.title = 'Archived';
        styles.backgroundColor = 'rgba(174, 177, 180, .15)';
        styles.borderLeftColor = '#AEB1B4';
        styles.icon = <ArchivedIcon preserveAspectRatio={'xMidYMid meet'} height='20px' width='20px' color='#AEB1B4' />;
        break;
      case "SoftwareUpgradeFailed":
        // Red
        styles.title = 'Software Upgrade Failed';
        styles.backgroundColor = 'rgba(213, 45, 52, .15)';
        styles.borderLeftColor = '#D52D34';
        styles.icon = <SoftwareUpgradeFailedIcon preserveAspectRatio={'xMidYMid meet'} height='20px' width='20px' />;
        break;
      default:
        break;
    }

    return styles;
  };

  const onSortClicked = () => {
    // switch between asc and desc
    setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
  };

  const handleSearchTextChanged = (event) => {
    // client-side search with new text
    setSearchText(event.target.value);
  };

  const onShowAircraftEntriesChanged = (event) => {
    setShowAircraftEntries(event.target.checked);
  }

  const historyEntriesToShow = props.cloudSystemDetails?.histories
  // filter by search text
    .filter(h => {
      if (!showAircraftEntries && ['ACIDConnected', 'ACIDTransferSession'].includes(h.type)) {
        // For performance reasons, strip out all aircraft entries unless the user manually turns them on
        return false;
      }
      var text = searchText.toLowerCase();
      return h.type.toLowerCase().includes(text) || h.message.toLowerCase().includes(text) || h.user.toLowerCase().includes(text);
    })
  // sort by order
    .sort((h1, h2) => {
      var time1 = new Date(h1.time).valueOf();
      var time2 = new Date(h2.time).valueOf();
  
      if (sortOrder === 'desc') {
        return time1 < time2 ? 1 : -1;
      } else {
        return time1 < time2 ? -1 : 1;
      }
    });

  return (
    <>
      <Box sx={{ px: 2, pb: 2, position: 'sticky', top: -24, backgroundColor: theme.palette.primary.main }}>
        <Grid container direction="row" justifyContent={'space-between'} alignItems={'center'}>
          <Grid item>
            <Typography fontSize={14} fontWeight={700} color={theme.palette.primary.contrastText}>
              History
            </Typography>
          </Grid>
          <Grid item>
            <Grid container direction="row" alignItems="center">
              <FormControl component="fieldset" style={{marginRight:10}}>
                <FormGroup aria-label="position" row>
                  <FormControlLabel
                    value="showaircraft"
                    control={<DarkSwitch checked={showAircraftEntries} onChange={onShowAircraftEntriesChanged}/>}
                    label="Show Aircraft Entries"
                    labelPlacement="start"
                    sx={{
                      color: theme.palette.sidebarContent.valueText,
                      "& .MuiFormControlLabel-label": {fontSize: 14},
                    }}
                  />
                </FormGroup>
              </FormControl>
              <Typography fontSize={14} color={theme.palette.primary.contrastText}>
                Last 90 Days
              </Typography>
              <IconButton
                onClick={onSortClicked}
                sx={{ color: theme.palette.primary.contrastText }}
              >
                {sortOrder === 'desc' ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
              </IconButton>
            </Grid>
          </Grid>
        </Grid>
        <Grid container direction={'row'}>
          <TextField 
            onChange={handleSearchTextChanged}
            placeholder={'Search'}
            value={searchText}
            fullWidth
            InputProps={{
              startAdornment: (
                <SearchIcon
                  sx={{ color: theme.palette.primary.contrastText }}
                />
              ),
            }}
            sx={{ 
              "& .MuiOutlinedInput-root":{"& > fieldset": {borderColor: theme.palette.secondary.main}},
              "& .MuiOutlinedInput-input.Mui-disabled":{WebkitTextFillColor: 'white'},
              input: { 
                color: theme.palette.primary.contrastText, 
              }, 
            }}
          />
        </Grid>
      </Box>
      <Paper
          elevation={0}
          sx={{
              p: 0,
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: theme.palette.primary.main
          }}
      >
        <VList>
          {historyEntriesToShow.map((historyEntry, index) => {

            var cardStyling = getCardStyling(historyEntry);

            return (
              <Box 
                key={index}
                sx={{ 
                  backgroundColor: cardStyling.backgroundColor, 
                  borderLeft: `5px solid ${cardStyling.borderLeftColor}`,
                  borderBottom: '1px solid #AEB1B4',
                  padding: 2
                }}
              >
                <Grid container direction='row'>
                  <Grid item sx={{width: '20px', height: '20px'}}>
                    {cardStyling.icon}
                  </Grid>
                  <Grid item sx={{ marginLeft: 1 }}>
                    {/* Symbol and Type */}
                    <Typography fontSize={12} color={cardStyling.textColor}>
                      {cardStyling.title}
                    </Typography>
                  </Grid>
                  <Grid item sx={{ marginLeft: 'auto' }}>
                    {/* Username and Date */}
                    <Grid container direction={'row'}>
                      <Grid item>
                        <Typography fontSize={12} fontStyle='italic' color={cardStyling.secondaryTextColor}>
                          {historyEntry.user}
                        </Typography>
                      </Grid>
                      <Grid item sx={{ ml: 1 }}>
                        <Typography fontSize={12} color={cardStyling.textColor}>
                          {moment(new Date(historyEntry.time)).format('MM/DD/YY hh:mm:ss a')}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid container direction='row' sx={{ mt: 1 }}>
                  <Grid item>
                    {/* Message */}
                    <Typography fontSize={12} color={cardStyling.secondaryTextColor} sx={{ whiteSpace: "pre-line" }}>
                      {historyEntry.message}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            );
          })}
        </VList>
      </Paper>
    </>
  );
};

export default SideBarHistory;